import React from "react";

/**
 * Écran de lancement sur-mesure de la PWA installée (mode standalone).
 * Fond blanc + logo officiel Junago centré (carré bleu nuit, J blanc, point violet).
 * Seul le point violet pulse ; le reste du logo reste immobile.
 * Disparaît dès que l'application est prête (aucun délai artificiel).
 * Ne s'affiche jamais en navigation web classique.
 */
export default function PwaSplash() {
  const isStandalone =
    typeof window !== "undefined" &&
    ((window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true);

  const [show, setShow] = React.useState(isStandalone);
  const [fading, setFading] = React.useState(false);

  React.useEffect(() => {
    if (!isStandalone) return;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setFading(true);
      window.setTimeout(() => setShow(false), 450);
    };
    const whenReady = () => {
      const fonts = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
      Promise.resolve(fonts).then(() => {
        requestAnimationFrame(() => requestAnimationFrame(finish));
      });
    };
    if (document.readyState === "complete") whenReady();
    else window.addEventListener("load", whenReady, { once: true });
    const cap = window.setTimeout(finish, 4000); // garde-fou : ne jamais rester bloqué
    return () => {
      window.removeEventListener("load", whenReady);
      window.clearTimeout(cap);
    };
  }, [isStandalone]);

  if (!show) return null;

  // Logo officiel : carré bleu nuit (teinte de l'icône installée), J blanc, point violet.
  const size = 96;
  return (
    <div
      data-testid="pwa-splash"
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: fading ? 0 : 1,
        transition: "opacity .45s ease",
      }}
    >
      <style>{`
        @keyframes jnDotPulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.32);opacity:.55}}
        .jn-splash-dot{transform-box:fill-box;transform-origin:center;animation:jnDotPulse 1.4s ease-in-out infinite}
        @media (prefers-reduced-motion: reduce){.jn-splash-dot{animation:none}}
      `}</style>
      <span
        className="inline-flex items-center justify-center shrink-0"
        style={{ width: size, height: size, borderRadius: 22, background: "#030D27" }}
      >
        <svg viewBox="0 0 32 32" width={size * 0.6} height={size * 0.6} fill="none">
          <path
            d="M20 5.5v13.5c0 4.142-3.358 7.5-7.5 7.5S5 23.142 5 19"
            stroke="#FFFFFF"
            strokeWidth="3.2"
            strokeLinecap="round"
            fill="none"
          />
          <circle className="jn-splash-dot" cx="24" cy="6.8" r="3" fill="#7C3AED" />
        </svg>
      </span>
    </div>
  );
}
